'use client'

import { datadogLogs, StatusType } from '@datadog/browser-logs'
import { DataDogBrowserLogsTransport } from '@loglayer/transport-datadog-browser-logs'
import { LogLayer, type LogLayerTransport } from 'loglayer'

import { reportSSRMisuse } from '../sentinel'
import type { ClientLoggerInitConfig } from '../types'
import { LOGLAYER_DEFAULT_CONFIG } from './constants'
import { LogLevel, type LogBuilder, type Logger, type LoggerContext } from './types'
import { LoggerWrapper } from './wrapper'

export interface ClientLoggerTransportFactories {
    console?: () => LogLayerTransport
    http?: () => LogLayerTransport
}

// Map our LogLevel enum to Datadog's StatusType
const mapLogLevelToStatusType = (logLevel: LogLevel): StatusType => {
    switch (logLevel) {
        case LogLevel.trace:
        case LogLevel.debug:
            return StatusType.debug
        case LogLevel.info:
            return StatusType.info
        case LogLevel.warn:
            return StatusType.warn
        case LogLevel.error:
            return StatusType.error
        case LogLevel.fatal:
            return StatusType.error
    }
}

// Uses Datadog browser SDK for console output and optional log shipping
const makeClientLogLayer = (
    config: ClientLoggerInitConfig,
    factories?: ClientLoggerTransportFactories,
): LogLayer => {
    const transports = []

    // Console transport - we always use this to log to browser console
    const consoleTransport =
        factories?.console?.() ??
        new DataDogBrowserLogsTransport({
            logger: datadogLogs.createLogger('console', {
                level: mapLogLevelToStatusType(config.logs.logLevel),
                handler: 'console',
            }),
        })
    transports.push(consoleTransport)

    // HTTP transport - ships logs to Datadog when enabled
    if (config.datadog.logs.enabled) {
        const httpTransport =
            factories?.http?.() ??
            new DataDogBrowserLogsTransport({
                enabled: config.datadog.logs.enabled,
                logger: datadogLogs.createLogger('http', {
                    level: mapLogLevelToStatusType(config.datadog.logs.logLevel),
                    handler: 'http',
                }),
            })
        transports.push(httpTransport)
    }

    return new LogLayer({
        ...LOGLAYER_DEFAULT_CONFIG,
        transport: transports,
    }).withContext({ name: 'client' })
}

/**
 * SSR sentinel for the LogBuilder interface.
 *
 * Terminal methods call `reportSSRMisuse('LogBuilder.<method>')`; fluent
 * methods return the sentinel silently — building up a log entry does no
 * browser-only work, so only *emitting* one during SSR is misuse. Silent
 * fluent returns also keep chains in prod (where reportSSRMisuse does not
 * throw) type-safe. The LogBuilder prefix distinguishes chained terminals
 * (`logger.withMetadata(...).info()`) from direct Logger terminals
 * (`logger.info()`) in Datadog filters.
 */
const sentinelLogBuilder: LogBuilder = {
    withMetadata(_metadata: Record<string, unknown>): LogBuilder {
        return sentinelLogBuilder
    },
    withError(_error: Error): LogBuilder {
        return sentinelLogBuilder
    },
    info(..._messages): void {
        reportSSRMisuse('LogBuilder.info')
    },
    warn(..._messages): void {
        reportSSRMisuse('LogBuilder.warn')
    },
    error(..._messages): void {
        reportSSRMisuse('LogBuilder.error')
    },
    debug(..._messages): void {
        reportSSRMisuse('LogBuilder.debug')
    },
    trace(..._messages): void {
        reportSSRMisuse('LogBuilder.trace')
    },
    fatal(..._messages): void {
        reportSSRMisuse('LogBuilder.fatal')
    },
}

/**
 * SSR sentinel for the Logger interface.
 *
 * Returned from `initializeClientLogger` when `typeof window === 'undefined'`
 * so the constructor never touches `window` during SSR. Terminal methods
 * call `reportSSRMisuse('Logger.<method>')`; fluent/factory methods
 * (`getLogger`, `withPrefix`, `withContext`, `withMetadata`, `withError`)
 * return the sentinel logger/builder silently — deriving a scoped logger
 * is exactly what `useLogger` does in render, which runs during SSR
 * prerender by design and is not misuse; the derived value is another
 * sentinel, so the contract is honored faithfully. `getContext` reports:
 * it is a read the sentinel cannot answer truthfully (no context is
 * tracked), and a silent `{}` could feed an SSR/client hydration mismatch.
 */
export function makeSentinelLogger(): Logger {
    const sentinel: Logger = {
        withPrefix(_prefix: string): Logger {
            return sentinel
        },
        withContext(_context: LoggerContext): Logger {
            return sentinel
        },
        getContext(): LoggerContext {
            reportSSRMisuse('Logger.getContext')
            return {}
        },
        withMetadata(_metadata: Record<string, unknown>): LogBuilder {
            return sentinelLogBuilder
        },
        withError(_error: Error): LogBuilder {
            return sentinelLogBuilder
        },
        getLogger(_context: LoggerContext): Logger {
            return sentinel
        },
        errorOnly(_error: Error, _opts?: { level?: LogLevel }): void {
            reportSSRMisuse('Logger.errorOnly')
        },
        metadataOnly(_metadata: Record<string, unknown>, _logLevel?: LogLevel): void {
            reportSSRMisuse('Logger.metadataOnly')
        },
        info(..._messages): void {
            reportSSRMisuse('Logger.info')
        },
        warn(..._messages): void {
            reportSSRMisuse('Logger.warn')
        },
        error(..._messages): void {
            reportSSRMisuse('Logger.error')
        },
        debug(..._messages): void {
            reportSSRMisuse('Logger.debug')
        },
        trace(..._messages): void {
            reportSSRMisuse('Logger.trace')
        },
        fatal(..._messages): void {
            reportSSRMisuse('Logger.fatal')
        },
    }
    return sentinel
}

export function initializeClientLogger(
    config: ClientLoggerInitConfig,
    factories?: ClientLoggerTransportFactories,
): Logger {
    if (typeof window === 'undefined') {
        return makeSentinelLogger()
    }
    const logLayer = makeClientLogLayer(config, factories).withContext({ module: 'root' })
    return new LoggerWrapper(logLayer)
}
